/**
 * "3일 전", "2시간 전" 같은 말.
 *
 * 누가 언제 봤는지는 **날짜보다 얼마나 지났는지**가 먼저 읽힌다.
 * `2026. 9. 27.`을 보면 오늘 날짜를 떠올려 빼야 하는데, "3일 전"은 그
 * 계산이 이미 끝난 값이다.
 *
 * ⚠️ 말은 `Intl.RelativeTimeFormat`에 맡긴다. 직접 "N일 전"을 만들면
 * 언어마다 복수형과 어순이 달라(영어는 `1 day ago`/`2 days ago`) 언어를
 * 더할 때마다 여기에 규칙이 쌓인다.
 */

/** 이보다 오래된 것은 상대 시간이 뜻을 잃는다 — "180일 전"보다 날짜가 낫다 */
const ABSOLUTE_AFTER_DAYS = 30;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function relativeTime(
  iso: string,
  language: string,
  /** 1분 안쪽일 때 쓸 말. 언어마다 다르므로 화면이 준다 */
  justNow: string,
  now: Date = new Date(),
): string {
  const then = new Date(iso);
  const gap = now.getTime() - then.getTime();

  /*
   * 시계가 어긋나 **미래**로 보이는 경우가 있다(기기 시각이 느린 경우).
   * "3분 후에 봄"은 거짓말이라, 그럴 때는 방금으로 접는다.
   */
  if (gap < MINUTE) {
    return justNow;
  }

  if (gap >= ABSOLUTE_AFTER_DAYS * DAY) {
    return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(then);
  }

  const format = new Intl.RelativeTimeFormat(language, { numeric: 'always' });
  if (gap < HOUR) {
    return format.format(-Math.floor(gap / MINUTE), 'minute');
  }
  if (gap < DAY) {
    return format.format(-Math.floor(gap / HOUR), 'hour');
  }
  return format.format(-Math.floor(gap / DAY), 'day');
}
